/**
 * chat.utils.tsx - Chat Utility Functions
 *
 * Provides utility functions for extracting and processing chat responses.
 */

import { Response } from '@/types/chat.types';

export const extractResponses = (
    responsesObject: Record<string, any> | undefined,
    setLatestResponseId: (id: string) => void,
    currentPostId?: string
): Response[] => {
    const response_list: Response[] = [];

    if (responsesObject !== undefined) {
        for (let key in responsesObject) {
            let value = responsesObject[key];
            response_list.push(value);
            // Only set latestResponseId if the response belongs to the current chat
            // If currentPostId is provided, check if response's thread root matches it
            // If no thread exists, assume it belongs to current chat (for backward compatibility)
            if (!currentPostId || !value.thread || value.thread.length === 0 || value.thread[0] === currentPostId) {
                setLatestResponseId(value._id);
            }
        }
    }

    return response_list;
};

export const extractResponseList = (
    item: Response | undefined,
    setLatestResponseId: (id: string) => void,
    currentPostId?: string
): Response[] => {
    return extractResponses(item?.responses, setLatestResponseId, currentPostId);
};

export const extractPostResponses = (
    post_responses: any | undefined,
    setLatestResponseId: (id: string) => void,
    currentPostId?: string
): Response[] => {
    return extractResponses(post_responses?.responses, setLatestResponseId, currentPostId);
};

/**
 * Recursively flattens all responses in a thread into a single array
 * Used to check AI processing status across all nested responses
 */
export const flattenAllResponses = (responses: Response[]): Response[] => {
    const flattened: Response[] = [];
    
    const flatten = (responseList: Response[]) => {
        for (const response of responseList) {
            flattened.push(response);
            if (response.responses) {
                const nestedResponses = Object.values(response.responses);
                flatten(nestedResponses);
            }
        }
    };
    
    flatten(responses);
    return flattened;
};
